
import { Type } from "@google/genai";
import type { Column, Insight, Explanation, StorySlide } from '../types';

// Make Gemini optional: if no API key, return safe fallbacks.
// Prefer Vite's `import.meta.env.VITE_*` when present, otherwise fall back to Node envs.
const API_KEY = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_GEMINI_API_KEY)
  || (typeof process !== 'undefined' ? (process.env.GEMINI_API_KEY || process.env.API_KEY) : undefined);
const hasApiKey = typeof API_KEY === 'string' && API_KEY.length > 0;
let ai: any = null;
const model = "gemini-2.5-flash";

async function getAi() {
    if (!hasApiKey) return null;
    if (ai) return ai;
    try {
        const mod: any = await import('@google/genai');
        const Ctor = mod.GoogleGenAI || mod.GoogleGenerativeAI || mod.default;
        if (typeof Ctor !== 'function') {
            console.warn('@google/genai client constructor not found; AI features will be disabled.');
            return null;
        }
        ai = new Ctor({ apiKey: API_KEY });
        return ai;
    } catch (err) {
        console.warn('Failed to initialize Gemini client:', err);
        return null;
    }
}

const insightsResponseSchema = {
    type: Type.OBJECT,
    properties: {
        insights: {
            type: Type.ARRAY,
            items: {
                type: Type.OBJECT,
                properties: {
                    column: { type: Type.STRING, description: "The primary column the insight is about." },
                    type: { type: Type.STRING, enum: ['anomaly', 'distribution', 'correlation', 'top_values', 'general'] },
                    title: { type: Type.STRING },
                    summary: { type: Type.STRING },
                    data: {
                        type: Type.OBJECT,
                        description: "Optional structured data. For 'correlation', include 'columnA' and 'columnB'. For 'anomaly', include 'value' and 'context' (the row, as a JSON string). For 'top_values' or 'distribution', include a 'values' array of objects with 'name' and 'value' properties.",
                        properties: {
                            columnA: { type: Type.STRING },
                            columnB: { type: Type.STRING },
                            value: { type: Type.STRING }, // Keeping it simple for the schema
                            context: { type: Type.STRING, description: "The full data row where the anomaly appeared, as a JSON string." },
                            values: {
                                type: Type.ARRAY,
                                items: {
                                    type: Type.OBJECT,
                                    properties: { name: { type: Type.STRING }, value: { type: Type.NUMBER } }
                                }
                            }
                        }
                    },
                },
                required: ['column', 'type', 'title', 'summary'],
            },
        },
    },
};

export const generateInsights = async (schema: Column[], data: Record<string, any>[]): Promise<Insight[]> => {
    if (!hasApiKey) {
        // No AI key: return no insights quietly
        return [];
    }
    const client = await getAi();
    if (!client) return [];
    const prompt = `
        You are an expert data analyst. Your task is to provide concise, actionable insights from a dataset sample.
        Analyze the provided schema and data to identify key patterns, anomalies, and distributions.
        Do not generate more than 5 insights. Focus on the most important findings.

        Dataset Schema:
        ${JSON.stringify(schema, null, 2)}

        Sample Data (first 30 rows):
        ${JSON.stringify(data.slice(0, 30), null, 2)}

        Based on this data, generate a list of insights. For each insight, provide:
        1. 'column': The primary column it relates to.
        2. 'type': The insight type ('anomaly', 'distribution', 'correlation', 'top_values', 'general').
        3. 'title': A clear, descriptive title.
        4. 'summary': A brief, easy-to-understand summary.
        5. 'data' (an optional object with structured data):
           - For 'correlation': Provide the two columns involved as 'columnA' and 'columnB'.
           - For 'anomaly': Provide the anomalous 'value' and its 'context' (the full data row where it appeared, as a JSON string).
           - For 'top_values' or 'distribution': Provide a 'values' array with the top 5 items and their counts (e.g., { "name": "Category A", "value": 15 }).
    `;

    try {
        const response = await client.models.generateContent({
            model,
            contents: prompt,
            config: {
                responseMimeType: "application/json",
                responseSchema: insightsResponseSchema,
                temperature: 0.2,
            },
        });

        const jsonText = response.text.trim();
        const parsedResponse = JSON.parse(jsonText);
        
        if (parsedResponse && Array.isArray(parsedResponse.insights)) {
            return parsedResponse.insights as Insight[];
        }

        return [];
    } catch (error) {
        console.error("Error calling Gemini API for insights:", error);
        throw new Error("Failed to get insights from Gemini API.");
    }
};

const explanationSchema = {
    type: Type.OBJECT,
    properties: {
        title: { type: Type.STRING },
        meaning: { type: Type.STRING },
        importance: { type: Type.STRING },
        nextSteps: { type: Type.STRING },
    },
    required: ['title', 'meaning', 'importance', 'nextSteps'],
};

export const explainInsight = async (insight: Insight): Promise<Explanation> => {
    if (!hasApiKey) {
        // Provide a simple, non-AI placeholder explanation
        return {
            title: insight.title,
            meaning: "AI explanation is disabled because no API key is configured.",
            importance: "You can still explore charts and dashboards without AI.",
            nextSteps: "Add GEMINI_API_KEY in .env.local to enable AI explanations.",
        };
    }
    const client = await getAi();
    if (!client) {
        return {
            title: insight.title,
            meaning: "AI client could not be initialized.",
            importance: "Insights are available, but AI explanations are disabled.",
            nextSteps: "Verify @google/genai is installed and a valid API key is configured.",
        };
    }
     const prompt = `
        You are a helpful data science coach. Explain the following data insight to a business user in simple terms.
        
        Insight Title: "${insight.title}"
        Insight Summary: "${insight.summary}"
        Insight Type: ${insight.type}
        Related Column(s): ${insight.data?.columnA ? `${insight.data.columnA}, ${insight.data.columnB}` : insight.column}

        Your response must be a JSON object. Provide an explanation with the following structure:
        - "title": A confirmation of the insight title.
        - "meaning": A simple definition of the insight. What does this finding actually mean?
        - "importance": The business implication or potential impact. Why does this matter?
        - "nextSteps": Suggest 2-3 concrete actions or further questions to investigate.

        Keep the language clear, concise, and non-technical. Frame the explanation to be empowering and actionable for someone who is not a data expert.
    `;
    try {
        const response = await client.models.generateContent({
            model,
            contents: prompt,
            config: {
                responseMimeType: "application/json",
                responseSchema: explanationSchema,
                temperature: 0.5,
            },
        });
        return JSON.parse(response.text.trim()) as Explanation;
    } catch (error) {
         console.error("Error calling Gemini API for explanation:", error);
        throw new Error("Failed to get explanation from Gemini API.");
    }
};

const storySlideSchema = {
    type: Type.OBJECT,
    properties: {
        title: { type: Type.STRING },
        narrative: { type: Type.STRING },
        visualizationSuggestion: { type: Type.STRING },
        keyNumbers: { type: Type.ARRAY, items: { type: Type.STRING } },
        callToAction: { type: Type.STRING },
    },
    required: ['title', 'narrative', 'visualizationSuggestion', 'keyNumbers'],
};

export const createStorySlide = async (insight: Insight): Promise<StorySlide> => {
    if (!hasApiKey) {
        // Provide a simple, non-AI placeholder story slide
        return {
            title: insight.title,
            narrative: "AI story generation is disabled (no API key).",
            visualizationSuggestion: "Pick a chart type that best matches the insight.",
            keyNumbers: [],
            callToAction: "Add GEMINI_API_KEY in .env.local to enable AI stories.",
        };
    }
    const client = await getAi();
    if (!client) {
        return {
            title: insight.title,
            narrative: "AI client could not be initialized.",
            visualizationSuggestion: "Use standard charts to build your story manually.",
            keyNumbers: [],
            callToAction: "Verify @google/genai install and API key.",
        };
    }
    const prompt = `
        You are a BI Storyteller. Transform the following data insight into a compelling "BI Story Slide".

        Insight:
        - Title: "${insight.title}"
        - Summary: "${insight.summary}"

        Generate a JSON object for the story slide with the following fields:
        - "title": A captivating headline for the slide.
        - "narrative": A short, one-sentence narrative that tells the story of the data.
        - "visualizationSuggestion": A recommendation for the best chart to visualize this (e.g., "Bar chart of sales by region").
        - "keyNumbers": An array of 2-3 key metrics or numbers that support the narrative.
        - "callToAction": (Optional) A clear, actionable next step or question for the audience.
    `;

     try {
        const response = await client.models.generateContent({
            model,
            contents: prompt,
            config: {
                responseMimeType: "application/json",
                responseSchema: storySlideSchema,
                temperature: 0.7,
            },
        });
        return JSON.parse(response.text.trim()) as StorySlide;
    } catch (error) {
         console.error("Error calling Gemini API for story slide:", error);
        throw new Error("Failed to get story slide from Gemini API.");
    }
}
